import alvieLogoExtendedFull from 'public/assets/images/Alvie-logo_extended-full.svg'
import { createAlertSVGElement } from 'shared/components/Alert/AlertSymbol'
import { makeErrorToast } from 'shared/components/Toaster'
import type { FontProps, SingleSelectExportLegendItem } from '../internalTypes'
import { errorLocal } from './generics'

export const truncateTextInContext = (
  text: string,
  width: number,
  maxTextWidth: number,
  context: CanvasRenderingContext2D
): string => {
  while (text.length > 0 && width > maxTextWidth) {
    text = text.slice(0, -1)
    width = context.measureText(text + '…').width
  }

  return text + '…'
}

export const getCanvasContext = (
  font?: string | FontProps
): { context: CanvasRenderingContext2D | null; canvas: HTMLCanvasElement } => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (context == null) {
    errorLocal('Canvas context not found!')
    makeErrorToast('Failed to build canvas')
    return { context: null, canvas }
  }

  setCanvasContextFont(context, font)

  return { canvas, context }
}

export const setCanvasContextFont = (
  context: CanvasRenderingContext2D,
  font?: string | FontProps
): CanvasRenderingContext2D => {
  if (font == null) {
    return context
  }

  if (typeof font === 'object') {
    context.font = `${font.fontWeight} ${font.fontSize} ${font.fontFamily}`
    if (font.letterSpacing != null) {
      context.letterSpacing = font.letterSpacing.toString()
    }
  } else {
    context.font = font
  }

  return context
}

export const getTextSize = (
  context: CanvasRenderingContext2D,
  texts: string[]
): { width: number; height: number; actualHeight: number } => {
  const { fontBoundingBoxAscent, fontBoundingBoxDescent } = context.measureText(texts[0])
  let maxWidth = 0
  let actualHeight = 0
  for (const text of texts) {
    const { width, actualBoundingBoxAscent, actualBoundingBoxDescent } = context.measureText(text)
    maxWidth = Math.max(maxWidth, width)
    actualHeight = Math.max(actualHeight, actualBoundingBoxAscent + actualBoundingBoxDescent)
  }
  return { width: maxWidth, height: fontBoundingBoxAscent + fontBoundingBoxDescent, actualHeight }
}

export const rotatedBoundingBox = (
  width: number,
  height: number,
  angleDegrees: number,
  maxSpace = 0
): { width: number; height: number } => {
  const angle = angleDegrees * (Math.PI / 180)

  const boundingWidth = Math.abs(width * Math.cos(angle)) + Math.abs(height * Math.sin(angle))
  const boundingHeight = Math.abs(width * Math.sin(angle)) + Math.abs(height * Math.cos(angle))

  if (maxSpace > 0 && boundingHeight > maxSpace) {
    return { width: Math.ceil(boundingWidth * (maxSpace / boundingHeight)), height: maxSpace }
  }

  return { width: Math.ceil(boundingWidth), height: Math.ceil(boundingHeight) }
}

export const calculateMaxTextWidth = ({
  maxSpace,
  lineHeight,
  wrapLines,
  angle
}: {
  maxSpace: number
  lineHeight: number
  wrapLines: number
  angle: number
}): number => {
  angle ??= 0

  if (angle === 0) {
    return maxSpace
  }

  wrapLines ??= 1
  const otherAngle = 90 - Math.abs(angle % 90)
  const lostSpace = lineHeight * wrapLines * Math.tan(otherAngle * (Math.PI / 180))

  return maxSpace / Math.abs(Math.sin(angle * (Math.PI / 180))) - lostSpace
}

export const setSVGAttribute = (element: SVGElement, attribute: string, value: string | number | undefined): void => {
  if (value == null) return
  if (typeof value === 'number') {
    element.setAttribute(attribute, value.toString())
  } else {
    element.setAttribute(attribute, value)
  }
}

export const createSVGTextItem = <DataKey extends string>(props: {
  text: string
  asLabel: boolean
  xOffset: number
  minWidth?: number
  context: CanvasRenderingContext2D
  container: SVGElement
  fontProps: FontProps
  alert?: SingleSelectExportLegendItem<DataKey>['alert']
}): { width: number; height: number; xOffset: number; yOffset: number } => {
  const { text, asLabel, xOffset, context, container, fontProps, alert } = props
  const fullText = asLabel ? text + ':' : text
  const {
    width: textWidth,
    fontBoundingBoxAscent,
    fontBoundingBoxDescent
  } = setCanvasContextFont(context, fontProps).measureText(text)
  const alertSize = alert == null ? 0 : fontBoundingBoxAscent + fontBoundingBoxDescent
  const extraLabelWidth = asLabel
    ? setCanvasContextFont(context, fontProps).measureText(':').width + alertSize * 0.5
    : 0
  const itemWidth = alert == null ? textWidth + extraLabelWidth : textWidth + alertSize * 1.5 + extraLabelWidth
  const minWidth = Math.max(props.minWidth ?? 0, itemWidth)

  const height = fontBoundingBoxAscent + fontBoundingBoxDescent
  const xText = xOffset + (minWidth - itemWidth)

  const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
  textElement.setAttribute('x', xText.toString())
  textElement.setAttribute('y', '.9em')
  setSVGAttribute(textElement, 'font-family', fontProps.fontFamily)
  setSVGAttribute(textElement, 'font-size', fontProps.fontSize)
  setSVGAttribute(textElement, 'font-weight', fontProps.fontWeight)
  setSVGAttribute(textElement, 'letter-spacing', fontProps.letterSpacing)
  setSVGAttribute(textElement, 'fill', fontProps.fill)
  textElement.textContent = alert == null ? fullText : text

  container.appendChild(textElement)

  if (alert != null) {
    const xAlert = xText + textWidth + alertSize / 2
    const alertElement = createAlertSVGElement(alert, {
      size: alertSize.toString(),
      x: xAlert.toString(),
      y: (alertSize * 0.05).toString()
    })

    container.appendChild(alertElement)

    if (asLabel) {
      const colonElement = textElement.cloneNode(true) as SVGTextElement
      colonElement.setAttribute('x', (xAlert + alertSize * 1.5).toString())
      colonElement.textContent = ':'
      container.appendChild(colonElement)
    }
  }

  return { width: minWidth, height, xOffset: xOffset + minWidth, yOffset: height }
}

export const loadSVGString = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${response.statusText}`)
    }
    return await response.text()
  } catch (error) {
    errorLocal('fetchSVG - Error fetching SVG:', error)
    return ''
  }
}

export const loadSVGElement = async (
  url: string
): Promise<{ svg: SVGSVGElement; width: number; height: number } | null> => {
  const svgString = await loadSVGString(url)

  // Parse the SVG string into a DOM element
  const parser = new DOMParser()
  const svgDoc: Document = parser.parseFromString(svgString, 'image/svg+xml')
  const svgElement = svgDoc.documentElement
  if (svgElement instanceof SVGSVGElement) {
    // Get logo dimensions from viewBox or width/height attributes
    const viewBox = svgElement.getAttribute('viewBox')?.split(' ') ?? []
    return {
      svg: svgElement,
      width: Number.parseFloat(svgElement.getAttribute('width') ?? viewBox[2] ?? '0'),
      height: Number.parseFloat(svgElement.getAttribute('height') ?? viewBox[3] ?? '0')
    }
  }

  return null
}

export interface SvgToPngOptions {
  /** CSS pixel width of the PNG. Defaults to intrinsic width from the SVG. */
  width?: number
  /** CSS pixel height of the PNG. Defaults to intrinsic height from the SVG. */
  height?: number
  /** Backing store scale; defaults to `window.devicePixelRatio` when defined, otherwise `1`. */
  pixelRatio?: number
}

const inferSvgDimensions = (svgRoot: SVGSVGElement): { width: number; height: number } => {
  const viewBoxValues =
    svgRoot
      .getAttribute('viewBox')
      ?.trim()
      .split(/[\s,]+/) ?? []
  const viewBoxWidth = viewBoxValues.length >= 4 ? Number.parseFloat(viewBoxValues[2] ?? '') : Number.NaN
  const viewBoxHeight = viewBoxValues.length >= 4 ? Number.parseFloat(viewBoxValues[3] ?? '') : Number.NaN
  const widthAttr = Number.parseFloat(svgRoot.getAttribute('width') ?? '')
  const heightAttr = Number.parseFloat(svgRoot.getAttribute('height') ?? '')

  const width = Number.isFinite(widthAttr) && widthAttr > 0 ? widthAttr : viewBoxWidth
  const height = Number.isFinite(heightAttr) && heightAttr > 0 ? heightAttr : viewBoxHeight

  const fallbackWidth = Number.isFinite(width) && width > 0 ? width : 24
  const fallbackHeight = Number.isFinite(height) && height > 0 ? height : 24

  return { width: fallbackWidth, height: fallbackHeight }
}

const toSerializableSvgRoot = (svg: string | SVGSVGElement): SVGSVGElement | null => {
  if (svg instanceof SVGSVGElement) {
    const clone = svg.cloneNode(true) as SVGSVGElement
    if (clone.getAttribute('xmlns') == null) {
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    }
    return clone
  }

  const trimmed = svg.trim()
  if (trimmed.length === 0) {
    return null
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(trimmed, 'image/svg+xml')
  const root = doc.documentElement
  if (!(root instanceof SVGSVGElement)) {
    return null
  }

  if (root.getAttribute('xmlns') == null) {
    root.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  }

  return root
}

/**
 * Rasterizes SVG markup or an {@link SVGSVGElement} to a PNG {@link Blob} in the browser.
 * For a data URL, use {@link FileReader.readAsDataURL} on the returned blob.
 */
export const svgToPng = async (svg: string | SVGSVGElement, options?: SvgToPngOptions): Promise<Blob | null> => {
  if (globalThis.window === undefined || globalThis.document === undefined) {
    errorLocal('svgToPng must run in a browser environment')
    return null
  }

  const root = toSerializableSvgRoot(svg)
  if (root == null) {
    errorLocal('svgToPng: invalid or empty SVG input')
    return null
  }

  const intrinsic = inferSvgDimensions(root)
  const cssWidth = options?.width ?? intrinsic.width
  const cssHeight = options?.height ?? intrinsic.height
  const browserWindow = globalThis.window
  const pixelRatio =
    options?.pixelRatio ??
    (browserWindow != null && browserWindow.devicePixelRatio > 0 ? browserWindow.devicePixelRatio : 1)

  const canvasWidth = Math.max(1, Math.ceil(cssWidth * pixelRatio))
  const canvasHeight = Math.max(1, Math.ceil(cssHeight * pixelRatio))

  const svgMarkup = new XMLSerializer().serializeToString(root)
  const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' })
  const objectUrl = URL.createObjectURL(svgBlob)

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = (): void => {
        resolve(img)
      }
      img.onerror = (): void => {
        reject(new Error('svgToPng: failed to decode SVG as image'))
      }
      img.src = objectUrl
    })

    const { canvas, context } = getCanvasContext()
    if (context == null) {
      return null
    }

    canvas.width = canvasWidth
    canvas.height = canvasHeight
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    context.clearRect(0, 0, cssWidth, cssHeight)
    context.drawImage(image, 0, 0, cssWidth, cssHeight)

    const pngBlob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob)
      }, 'image/png')
    })

    if (pngBlob == null) {
      errorLocal('svgToPng: canvas.toBlob returned null')
    }

    return pngBlob
  } catch (error) {
    errorLocal('svgToPng:', error)
    return null
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const calculateSpaceFromAngledLine = ({
  length,
  angle
}: {
  length: number
  angle: number
}): { horizontal: number; vertical: number } => {
  switch (Math.abs(angle) % 180) {
    case 0:
      return { horizontal: length, vertical: 0 }
    case 90:
      return { horizontal: 0, vertical: length }
    default:
      return {
        horizontal: Math.max(length * Math.abs(Math.tan(angle * (Math.PI / 180))), length),
        vertical: Math.max(length * Math.abs(Math.tan((angle - 90) * (Math.PI / 180))), length)
      }
  }
}

export const handleFontInSVGString = (
  svgString: string,
  embeddedFontStyle: string | null,
  fallbackFontFamily: string
): string => {
  if (embeddedFontStyle == null) {
    return svgString.replaceAll(/font-family="Graphik-(Medium|Regular)"/g, `font-family="${fallbackFontFamily}"`)
  } else {
    return svgString.replace(/^(<svg[^>]*>)/, `$1${embeddedFontStyle}`)
  }
}

export const addLogoToSVG = async ({
  svgWidth,
  rightMargin,
  y,
  logoHeight,
  minYOffset,
  bottomPadding,
  container
}: {
  svgWidth: number
  rightMargin: number
  y: number
  logoHeight: number
  minYOffset: number
  bottomPadding: number
  container: SVGElement
}): Promise<number> => {
  const loadedLogo = await loadSVGElement(alvieLogoExtendedFull.src)
  if (loadedLogo == null) {
    errorLocal('Failed to load logo!')
    return y + minYOffset + bottomPadding
  } else {
    const { svg: logoElement, width: originalLogoWidth, height: originalLogoHeight } = loadedLogo
    const scale = logoHeight / originalLogoHeight
    const logoGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    logoGroup.setAttribute('class', 'chart-logo')
    // Copy the SVG's children, not the SVG element itself
    for (const child of Array.from(logoElement.children)) {
      logoGroup.appendChild(child.cloneNode(true))
    }
    logoGroup.setAttribute(
      'transform',
      `translate(${svgWidth - originalLogoWidth * scale - rightMargin}, 0) scale(${scale})`
    )
    container.appendChild(logoGroup)
    return y + Math.max(minYOffset, logoHeight) + bottomPadding
  }
}
